const initializedContainers = new WeakSet();

function wrapTranslate(value, cycle) {
    if (cycle <= 0) {
        return value;
    }
    let v = value;
    while (v <= -cycle) {
        v += cycle;
    }
    while (v > 0) {
        v -= cycle;
    }
    return v;
}

function ensureMarqueeDuplicate(track, slideSelector) {
    if (track.dataset.marqueeDuplicated === '1') {
        return;
    }
    const originals = Array.from(track.querySelectorAll(`${slideSelector}:not([data-marquee-clone])`));
    if (originals.length < 1) {
        return;
    }
    originals.forEach(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.marqueeClone = '1';
        track.appendChild(clone);
    });
    track.dataset.marqueeDuplicated = '1';
}

function layoutSlideWidths(container, track, slideSelector, getSpvAndGap) {
    const w = container.clientWidth;
    if (!w) {
        return;
    }
    const cfg = getSpvAndGap(w);
    const gap = cfg.gap;
    let slideW;
    if (
        typeof cfg.slideWidthPx === 'number' &&
        Number.isFinite(cfg.slideWidthPx)
    ) {
        slideW = cfg.slideWidthPx;
    } else {
        const spv = cfg.spv;
        slideW = (w - gap * (spv - 1)) / spv;
    }
    track.style.gap = `${gap}px`;
    track.querySelectorAll(slideSelector).forEach(slide => {
        slide.style.width = `${Math.max(0, slideW)}px`;
        slide.style.flexShrink = '0';
    });
}

function measureCycleWidth(track) {
    const firstClone = track.querySelector('[data-marquee-clone="1"]');
    if (firstClone) {
        return firstClone.offsetLeft;
    }
    if (track.dataset.marqueeDuplicated === '1') {
        return track.scrollWidth / 2;
    }
    return 0;
}

function bindImageLoadRemeasure(track, remeasure) {
    track.querySelectorAll('img').forEach(img => {
        if (img.complete || img.dataset.marqueeImgBound === '1') {
            return;
        }
        img.dataset.marqueeImgBound = '1';
        img.addEventListener(
            'load',
            () => {
                remeasure();
            },
            { once: true }
        );
    });
}

/**
 * Stały drift w lewo + pauza hover (mysz) + przeciąganie w obie strony (pointer).
 *
 * @param {object} opts
 * @param {string} opts.containerSelector - root wiersza (np. .js-logo-slider)
 * @param {string} opts.trackSelector - bezpośrednie dziecko z wierszem slajdów (względem container)
 * @param {string} opts.slideSelector - klasa slajdu
 * @param {(width: number) => { spv?: number, gap: number, slideWidthPx?: number }} opts.getSpvAndGap
 *   — albo { spv, gap } (szerokość z „slides per view”), albo { slideWidthPx, gap } (stała szerokość slajdu)
 * @param {number} opts.speedPxPerSecond - 0 = brak auto (np. prefers-reduced-motion)
 * @param {(container: HTMLElement, track: HTMLElement) => void} [opts.afterLayout] - np. wyrównanie wysokości kart
 * @param {boolean} [opts.remeasureOnImageLoad] - ponowny pomiar po załadowaniu obrazów w tracku
 */
export function initInfiniteHorizontalMarquee(opts) {
    const {
        containerSelector,
        trackSelector,
        slideSelector,
        getSpvAndGap,
        speedPxPerSecond,
        afterLayout,
        remeasureOnImageLoad = false,
    } = opts;

    document.querySelectorAll(containerSelector).forEach(container => {
        const track = container.querySelector(trackSelector);
        if (!track || initializedContainers.has(container)) {
            return;
        }

        const originals = track.querySelectorAll(`${slideSelector}:not([data-marquee-clone])`);
        if (originals.length < 1) {
            return;
        }

        ensureMarqueeDuplicate(track, slideSelector);
        initializedContainers.add(container);

        let x = 0;
        let cycleWidth = 0;
        let pausedHover = false;
        let dragging = false;
        let dragPointerId = null;
        let dragStartClientX = 0;
        let dragStartTranslate = 0;
        let lastTs = performance.now();

        const applyTransform = () => {
            track.style.transform = `translate3d(${x}px,0,0)`;
        };

        const remeasure = () => {
            layoutSlideWidths(container, track, slideSelector, getSpvAndGap);
            if (typeof afterLayout === 'function') {
                afterLayout(container, track);
            }
            if (remeasureOnImageLoad) {
                bindImageLoadRemeasure(track, remeasure);
            }
            cycleWidth = measureCycleWidth(track);
            x = wrapTranslate(x, cycleWidth);
            applyTransform();
        };

        const rafLoop = now => {
            const dt = Math.min(0.064, (now - lastTs) / 1000);
            lastTs = now;

            if (!dragging && !pausedHover && speedPxPerSecond > 0 && cycleWidth > 0) {
                x -= speedPxPerSecond * dt;
                while (x <= -cycleWidth) {
                    x += cycleWidth;
                }
            }
            applyTransform();
            requestAnimationFrame(rafLoop);
        };

        requestAnimationFrame(rafLoop);

        remeasure();

        const ro = new ResizeObserver(() => remeasure());
        ro.observe(container);
        window.addEventListener('load', remeasure);

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(remeasure);
        }

        container.addEventListener('mouseenter', () => {
            if (window.matchMedia('(pointer: fine)').matches) {
                pausedHover = true;
            }
        });
        container.addEventListener('mouseleave', () => {
            pausedHover = false;
        });

        container.addEventListener('pointerdown', e => {
            if (e.button !== 0) {
                return;
            }
            dragging = true;
            dragPointerId = e.pointerId;
            dragStartClientX = e.clientX;
            dragStartTranslate = x;
            try {
                container.setPointerCapture(e.pointerId);
            } catch (_) {
                /* ignore */
            }
        });

        container.addEventListener('pointermove', e => {
            if (!dragging || e.pointerId !== dragPointerId) {
                return;
            }
            const dx = e.clientX - dragStartClientX;
            x = wrapTranslate(dragStartTranslate + dx, cycleWidth || measureCycleWidth(track));
        });

        const endDrag = e => {
            if (!dragging || (e && e.pointerId !== dragPointerId)) {
                return;
            }
            dragging = false;
            dragPointerId = null;
            try {
                if (e && e.pointerId != null) {
                    container.releasePointerCapture(e.pointerId);
                }
            } catch (_) {
                /* ignore */
            }
        };

        container.addEventListener('pointerup', endDrag);
        container.addEventListener('pointercancel', endDrag);
        container.addEventListener('lostpointercapture', () => {
            dragging = false;
            dragPointerId = null;
        });
    });
}
